/**
 * ============================================================================
 * DISPUTE CHAT COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Real-time messaging component for dispute communication
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDispute } from '../../hooks/useDispute';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import { useAuthContext } from '../../providers';
import Button from '../common/Button';
import Textarea from '../common/Textarea';
import Avatar from '../common/Avatar';
import Spinner from '../common/Spinner';

/**
 * Dispute Chat Component
 * @param {Object} props
 * @param {string} props.disputeId - Dispute ID
 * @param {Array} props.messages - Initial messages
 * @param {Function} props.onNewMessage - Callback when new message is sent
 * @returns {JSX.Element}
 */
const DisputeChat = ({ disputeId, messages: initialMessages = [], onNewMessage }) => {
  const { sendMessage, loading } = useDispute();
  const { showError } = useToast();
  const { formatDate, formatTime } = useI18n();
  const { user } = useAuthContext();
  const [messages, setMessages] = useState(initialMessages);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    setMessages(initialMessages);
  }, [initialMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || loading) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      await sendMessage(disputeId, messageText);
      
      // Add optimistic update
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message: messageText,
        senderId: user?.id,
        senderName: user?.fullName || user?.name || 'You',
        createdAt: new Date().toISOString(),
        isTemporary: true,
      };
      
      setMessages((prev) => [...prev, tempMessage]);
      
      if (onNewMessage) {
        onNewMessage(tempMessage);
      }
    } catch (error) {
      showError(error.message || 'Failed to send message');
      setNewMessage(messageText); // Restore message on error
    }
  };

  const isMyMessage = (message) => {
    return message.senderId === user?.id || message.senderName === (user?.fullName || user?.name);
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-xl">💬</span>
          Messages
        </h3>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          Communicate with the other party
        </p>
      </div>

      {/* Messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50"
      >
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💬</div>
            <p className="text-gray-600 dark:text-gray-400">
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          messages.map((message, index) => {
            const isMine = isMyMessage(message);
            const showAvatar = index === 0 || messages[index - 1]?.senderId !== message.senderId;
            const showDate = index === 0 || 
              new Date(message.createdAt).toDateString() !== 
              new Date(messages[index - 1]?.createdAt).toDateString();

            return (
              <div key={message.id || index}>
                {/* Date Separator */}
                {showDate && (
                  <div className="text-center my-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-3 py-1 rounded-full">
                      {formatDate(message.createdAt)}
                    </span>
                  </div>
                )}

                {/* Message */}
                <div
                  className={`flex items-start gap-3 ${
                    isMine ? 'flex-row-reverse' : 'flex-row'
                  } ${showAvatar ? 'mt-4' : 'mt-1'}`}
                >
                  {/* Avatar */}
                  {showAvatar && (
                    <Avatar
                      name={message.senderName || 'User'}
                      size="sm"
                      className="flex-shrink-0"
                    />
                  )}
                  {!showAvatar && <div className="w-8" />}

                  {/* Message Bubble */}
                  <div
                    className={`max-w-[70%] ${
                      isMine ? 'items-end' : 'items-start'
                    } flex flex-col`}
                  >
                    {showAvatar && (
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 px-2">
                        {isMine ? 'You' : message.senderName || 'User'}
                      </span>
                    )}
                    <div
                      className={`
                        rounded-2xl px-4 py-2 shadow-md
                        ${
                          isMine
                            ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-br-sm'
                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-bl-sm'
                        }
                        ${message.isTemporary ? 'opacity-70' : ''}
                        animate-fadeInUp
                      `}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {message.message}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 px-2">
                      {formatTime(message.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            rows={2}
            className="flex-1 resize-none"
            maxLength={1000}
          />
          <Button
            type="submit"
            variant="gradient"
            disabled={!newMessage.trim() || loading}
            className="self-end"
            size="lg"
          >
            {loading ? (
              <Spinner size="sm" variant="default" />
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-right">
          {newMessage.length}/1000 characters
        </p>
      </form>
    </div>
  );
};

export default DisputeChat;
